import { GetContract } from "@/services/fhir/contract/GetContract";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useOrganizationContext } from "./OrganizationContext";
import { Contract } from "fhir/r5";
import { GetTask } from "@/services/fhir/task/GetTask";
import { GetPaymentNotice } from "@/services/fhir/financial/GetPaymentNotice";

type ContractResource = Contract;

interface CRMContractContextValue {
  contractCRM: ContractResource | null;
  loading: boolean;
  tasksCRM: any | null;
  paymentNoticeCRM: any | null;
  fetchAllCRMData: () => Promise<void>;
}

interface CRMContractContextProviderProps {
  children: React.ReactNode;
}

export const CRMContractContext = createContext<CRMContractContextValue>(
  {} as CRMContractContextValue,
);

export function CRMContractContextProvider({
  children,
}: CRMContractContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractCRM, setContractCRM] = useState<ContractResource | null>(null);
  const [tasks, setTasks] = useState<any | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<any | null>(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingPaymentNotice, setLoadingPaymentNotice] = useState(false);

  const fetchContract = useCallback(async () => {
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractCRM(null);
        return null;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "crm50,crm100",
      });
      setContractCRM(contractData?.entry?.[0]?.resource);

      return contractData?.entry?.[0]?.resource;
    } catch (error) {
      console.error("Erro ao buscar contrato de CRM:", error);
      setContractCRM(null);
      return null;
    }
  }, [organization]);

  const fetchTasks = useCallback(
    async (contract: ContractResource) => {
      try {
        if (!organization?.id || !contract?.id) {
          console.warn("Nenhuma organização ou contrato selecionado");
          setTasks(null);
          return;
        }

        const tasks = await GetTask({
          owner: organization.id,
          sort: "-authored-on",
        });
        setTasks(tasks?.entry?.map((task: any) => task.resource) || []);
      } catch (error) {
        console.error("Erro ao buscar tarefas de CRM:", error);
        setTasks(null);
      }
    },
    [organization],
  );

  const fetchPaymentNotice = useCallback(
    async (contract: ContractResource) => {
      try {
        if (!organization?.id || !contract?.id) {
          console.warn("Nenhuma organização ou contrato selecionado");
          setPaymentNotice(null);
          return;
        }

        const paymentNotice = await GetPaymentNotice({
          contractId: contract.id,
          sort: "-_lastUpdated",
        });

        setPaymentNotice(
          paymentNotice?.entry?.map((payment: any) => payment.resource) || [],
        );
      } catch (error) {
        console.error("Erro ao buscar payment notices de CRM:", error);
        setPaymentNotice(null);
      }
    },
    [organization],
  );

  const fetchAllCRMData = useCallback(async () => {
    setLoadingContract(true);
    setLoadingTasks(true);
    setLoadingPaymentNotice(true);

    try {
      // 1. Primeiro busca o contrato
      const contractResult = await fetchContract();

      // 2. Se tiver contrato, busca as tasks e payment notices em paralelo
      if (contractResult?.id) {
        await Promise.all([
          fetchTasks(contractResult),
          fetchPaymentNotice(contractResult),
        ]);
      }
    } catch (error) {
      console.error("Erro ao buscar dados do CRM:", error);
    } finally {
      setLoadingContract(false);
      setLoadingTasks(false);
      setLoadingPaymentNotice(false);
    }
  }, [fetchContract, fetchTasks, fetchPaymentNotice]);

  // Único useEffect para gerenciar todas as chamadas
  useEffect(() => {
    fetchAllCRMData();
  }, [fetchAllCRMData]);

  const contextValue: CRMContractContextValue = {
    loading: loadingContract || loadingTasks || loadingPaymentNotice,
    contractCRM,

    fetchAllCRMData,

    tasksCRM: tasks,
    paymentNoticeCRM: paymentNotice,
  };

  return (
    <CRMContractContext.Provider value={contextValue}>
      {children}
    </CRMContractContext.Provider>
  );
}

export function useCRMContractContext() {
  return useContext(CRMContractContext);
}
